import type { ReactNode } from "react";
import type { EditorState } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";

import { isEmbeddableUrl } from "../commands/media";
import { applyPasteMenuChoice, closePasteMenu, pasteMenuState } from "../paste";
import { MenuPopover, ToolbarButton, placementAtPos } from "./MenuPopover";

export interface PasteMenuProps {
  view: EditorView;
  state: EditorState;
}

/**
 * After a URL is pasted it becomes a link, and this menu offers the two other
 * readings (`editor.md` §8.3): an embed, where the host allows framing, or a
 * mention. A paste of several URLs at once can become a run of embeds in one
 * action. The menu is transient — the next edit or selection change closes it.
 */
export function PasteMenu({ view, state }: PasteMenuProps): ReactNode {
  const menu = pasteMenuState(state);
  if (!menu) return null;
  const embeddable = (menu.urls ?? [menu.href]).some((url) => isEmbeddableUrl(url));
  const many = (menu.urls?.length ?? 0) > 1;

  return (
    <MenuPopover
      placement={placementAtPos(view, menu.from)}
      label="Pasted link"
      testId="paste-menu"
      onDismiss={() => {
        closePasteMenu(view);
      }}
    >
      <div className="wiki-menu__row">
        <ToolbarButton
          label="Keep as link"
          onSelect={() => {
            applyPasteMenuChoice(view, menu, "link");
          }}
        />
        {embeddable && (
          <ToolbarButton
            label={many ? "Embed all" : "Embed"}
            onSelect={() => {
              applyPasteMenuChoice(view, menu, "embed");
            }}
          />
        )}
        {!many && (
          <ToolbarButton
            label="Mention"
            onSelect={() => {
              applyPasteMenuChoice(view, menu, "mention");
            }}
          />
        )}
      </div>
    </MenuPopover>
  );
}
